// This file contains utility functions for customer reports visualization

// Mapbox access token - in production, use environment variable
export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibmF1bWFuNzgiLCJhIjoiY21idGMyM2xiMDJiMDJxc2FxYXYyeW13dSJ9.1ucE7demOYLdVRes-35p3Q';
// Jitter amount for customer points (in degrees)
export const JITTER_AMOUNT = 0.005; 

// Helper function to create GeoJSON data from city-based location data
export const createGeoJsonData = async (data) => {
  // Handle different input formats
  let locations;
  
  // If input is the full API response object with locations property
  if (data && data.locations && Array.isArray(data.locations)) {
    locations = data.locations;
  } 
  // If input is directly the locations array
  else if (Array.isArray(data)) {
    locations = data;
  } 
  // Invalid input
  else {
    return { type: 'FeatureCollection', features: [] };
  }
  
  if (locations.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }
  
  // Filter out invalid locations
  const validLocations = locations.filter(loc => {
    // Skip locations with missing city or state
    // But allow '-' as a valid city name since it appears in the data
    if ((loc.city === undefined || loc.city === null || (loc.city === '' && loc.city !== '-')) || 
        !loc.state) {
      return false;
    }
    return true;
  });
  
  // Create an array to hold all city points
  const features = [];
  
  // Process in batches to avoid overwhelming the browser
  const batchSize = 50;
  
  // Process each batch
  for (let i = 0; i < validLocations.length; i += batchSize) {
    const batch = validLocations.slice(i, i + batchSize);
    
    // Process each location in the batch
    for (const location of batch) {
      try {
        // Parse numeric values with fallbacks
        const customerCount = parseInt(location.customer_count) || 1;
        const orderCount = parseInt(location.order_count) || customerCount;
        const totalSales = parseFloat(location.total_sales) || 0;
        const ordersPerCustomer = orderCount / customerCount;
        
        // Get coordinates from backend
        let coordinates = location.coordinates;
        
        // Skip locations without coordinates
        if (!coordinates || !Array.isArray(coordinates) || coordinates.length !== 2) {
          continue;
        }
        
        
        // Create a GeoJSON feature
        features.push({
          type: 'Feature',
          properties: {
            id: `${location.city}-${location.state}-${Math.round(Math.random() * 100000)}`,
            city: location.city,
            state: location.state,
            customer_count: customerCount,
            total_sales: totalSales,
            order_count: orderCount,
            avg_order_value: parseFloat(location.avg_order_value) || 0,
            orders_per_customer: ordersPerCustomer,
            repeat_purchase_rate: parseFloat(location.repeat_purchase_rate) || 0
          },
          geometry: {
            type: 'Point',
            coordinates
          }
        });
      } catch (error) {
        console.error(`Error creating GeoJSON feature for ${location.city}, ${location.state}:`, error);
      }
    }
  }
  
  return {
    type: 'FeatureCollection',
    features
  };
};

// Get color based on profit margin
export const getProfitMarginColor = (margin) => {
  if (margin >= 30) return '#00C853'; // High profit - green
  if (margin >= 15) return '#FFD600'; // Medium profit - yellow
  return '#FF3D00'; // Low profit - red
};

// Get radius based on customer count
export const getCustomerCountRadius = (count) => {
  // Base size + logarithmic scaling for better visualization
  return Math.max(5, Math.min(20, 5 + Math.log10(count) * 3));
};

// Convert state abbreviation to full state name
export const getFullStateName = (abbr) => {
  const stateMap = {
    'AL': 'Alabama',
    'AK': 'Alaska',
    'AZ': 'Arizona',
    'AR': 'Arkansas',
    'CA': 'California',
    'CO': 'Colorado',
    'CT': 'Connecticut',
    'DE': 'Delaware',
    'FL': 'Florida',
    'GA': 'Georgia',
    'HI': 'Hawaii',
    'ID': 'Idaho',
    'IL': 'Illinois',
    'IN': 'Indiana',
    'IA': 'Iowa',
    'KS': 'Kansas',
    'KY': 'Kentucky',
    'LA': 'Louisiana',
    'ME': 'Maine',
    'MD': 'Maryland',
    'MA': 'Massachusetts',
    'MI': 'Michigan',
    'MN': 'Minnesota',
    'MS': 'Mississippi',
    'MO': 'Missouri',
    'MT': 'Montana',
    'NE': 'Nebraska',
    'NV': 'Nevada',
    'NH': 'New Hampshire',
    'NJ': 'New Jersey',
    'NM': 'New Mexico',
    'NY': 'New York',
    'NC': 'North Carolina',
    'ND': 'North Dakota',
    'OH': 'Ohio',
    'OK': 'Oklahoma',
    'OR': 'Oregon',
    'PA': 'Pennsylvania',
    'RI': 'Rhode Island',
    'SC': 'South Carolina',
    'SD': 'South Dakota',
    'TN': 'Tennessee',
    'TX': 'Texas',
    'UT': 'Utah',
    'VT': 'Vermont',
    'VA': 'Virginia',
    'WA': 'Washington',
    'WV': 'West Virginia',
    'WI': 'Wisconsin',
    'WY': 'Wyoming',
    'DC': 'District of Columbia',
    'AS': 'American Samoa',
    'GU': 'Guam',
    'MP': 'Northern Mariana Islands',
    'PR': 'Puerto Rico',
    'VI': 'U.S. Virgin Islands',
    'AB': 'Alberta',
    'BC': 'British Columbia',
    'MB': 'Manitoba',
    'NB': 'New Brunswick',
    'NL': 'Newfoundland and Labrador',
    'NS': 'Nova Scotia',
    'NT': 'Northwest Territories',
    'NU': 'Nunavut',
    'ON': 'Ontario',
    'PE': 'Prince Edward Island',
    'QC': 'Quebec',
    'SK': 'Saskatchewan',
    'YT': 'Yukon'
  };
  
  return stateMap[abbr] || abbr; // Return the full name if found, otherwise return the abbreviation
};
